import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { SAVE_BUTTON_STATES } from '@urbanx/agx.ui-components2'
import { AgxToastState } from 'types/commonTypes'
import {
  SolicitorDetails,
  VendorModel,
  useInvalidateUseStaffGetPropertyInformationProgressForCampaign,
  useInvalidateUseStaffGetVendorDetailsForCampaign,
  useStaffGetVendorDetailsForCampaign,
  useStaffUpdateVendorDetailsForCampaign,
} from '@urbanx/agx.campaigns.client'
import { isDirtyEqual } from 'utils/compares'
import { useGetListingByCampaignId } from '@urbanx/agx.rex.client'
import { getRexFieldChangeChecks } from 'pages/campaigns/components/manageCampaign/RexDataUpdateHelper'
import useRexDisconnectedModal from 'pages/campaigns/components/manageCampaign/useRexDisconnectedModal'

interface UseVendorDetailsSectionStateArgs {
  campaignId?: string
  updateToastState: (toastState: AgxToastState) => void
}

const ignoredVendorFields = ['type', 'stakeholderType']

interface VendorDetailsSectionState {
  vendorDetails: VendorModel[]
  setVendorDetails: (value: VendorModel[]) => void
  solicitorDetails: SolicitorDetails | undefined
  setSolicitorDetails: (value: SolicitorDetails | undefined) => void
  hasUnsavedChanges: boolean
  saveState: (typeof SAVE_BUTTON_STATES)[keyof typeof SAVE_BUTTON_STATES]
  isSaving: boolean
  isPending: boolean
  isError: boolean
  handleSaveChanges: () => Promise<void>
  rexDisconnectedModal: ReactNode
}

const useVendorDetailsSectionState = ({
  campaignId,
  updateToastState,
}: UseVendorDetailsSectionStateArgs): VendorDetailsSectionState => {
  const [vendorDetails, setVendorDetails] = useState<VendorModel[]>([])
  const [solicitorDetails, setSolicitorDetails] = useState<
    SolicitorDetails | undefined
  >()
  const [isSaving, setIsSaving] = useState(false)
  const [hasSaved, setHasSaved] = useState(false)
  const [hasError, setHasError] = useState(false)
  const { modal: rexDisconnectedModal, openWithChanges } =
    useRexDisconnectedModal()

  const {
    data: vendorDetailsResponse,
    isPending,
    isError,
  } = useStaffGetVendorDetailsForCampaign(campaignId ?? '', !!campaignId)

  const invalidateStaffGetVendorDetails =
    useInvalidateUseStaffGetVendorDetailsForCampaign()
  const invalidateStaffGetPropertyInformationProgress =
    useInvalidateUseStaffGetPropertyInformationProgressForCampaign()
  const { data: rexListing, isPending: isPendingRexListing } =
    useGetListingByCampaignId(campaignId ?? '', !!campaignId)

  const { staffUpdateVendorDetailsForCampaign } =
    useStaffUpdateVendorDetailsForCampaign({
      onSuccess: () => undefined,
      onError: () => undefined,
    })

  useEffect(() => {
    setVendorDetails(vendorDetailsResponse?.vendors ?? [])
  }, [vendorDetailsResponse?.vendors])

  useEffect(() => {
    setSolicitorDetails(vendorDetailsResponse?.vendorSolicitor)
  }, [vendorDetailsResponse?.vendorSolicitor])

  const hasUnsavedChanges = useMemo(
    () =>
      !(
        isDirtyEqual(
          vendorDetails,
          vendorDetailsResponse?.vendors ?? [],
          ignoredVendorFields
        ) &&
        isDirtyEqual(solicitorDetails, vendorDetailsResponse?.vendorSolicitor)
      ),
    [
      solicitorDetails,
      vendorDetailsResponse?.vendorSolicitor,
      vendorDetails,
      vendorDetailsResponse?.vendors,
    ]
  )

  useEffect(() => {
    if (hasUnsavedChanges) {
      setHasSaved(false)
      setHasError(false)
    }
  }, [hasUnsavedChanges])

  const saveState = useMemo(() => {
    if (isSaving) return SAVE_BUTTON_STATES.SAVING
    if (hasError) return SAVE_BUTTON_STATES.ERROR
    if (hasSaved) return SAVE_BUTTON_STATES.SAVED
    if (hasUnsavedChanges) return SAVE_BUTTON_STATES.UNSAVED
    return SAVE_BUTTON_STATES.UNSAVED
  }, [hasError, hasSaved, hasUnsavedChanges, isSaving])

  const handleSaveChanges = useCallback(async () => {
    if (!campaignId || !hasUnsavedChanges || isSaving) return

    setIsSaving(true)
    setHasError(false)
    try {
      if (!rexListing && isPendingRexListing === false) {
        const changes = [
          ...getRexFieldChangeChecks('Vendors', vendorDetails, {
            Vendors: vendorDetailsResponse?.vendors ?? [],
          }),
          ...getRexFieldChangeChecks('VendorSolicitor', solicitorDetails, {
            VendorSolicitor: vendorDetailsResponse?.vendorSolicitor,
          }),
        ]
          .filter(({ changed }) => changed)
          .map(({ field, value }) => ({ field, value }))

        openWithChanges(changes)
      }

      await staffUpdateVendorDetailsForCampaign({
        campaignId,
        body: {
          vendors: vendorDetails,
          vendorSolicitor: solicitorDetails,
        },
      })
      await Promise.all([
        invalidateStaffGetVendorDetails(campaignId),
        invalidateStaffGetPropertyInformationProgress(campaignId),
      ])
      setHasSaved(true)
      updateToastState({
        color: 'success',
        message: 'Vendor details saved successfully',
        open: true,
      })
    } catch {
      setHasError(true)
      updateToastState({
        color: 'error',
        message: 'Error saving vendor details',
        open: true,
      })
    } finally {
      setIsSaving(false)
    }
  }, [
    campaignId,
    hasUnsavedChanges,
    isSaving,
    isPendingRexListing,
    solicitorDetails,
    openWithChanges,
    rexListing,
    staffUpdateVendorDetailsForCampaign,
    invalidateStaffGetVendorDetails,
    invalidateStaffGetPropertyInformationProgress,
    updateToastState,
    vendorDetails,
    vendorDetailsResponse?.vendorSolicitor,
    vendorDetailsResponse?.vendors,
  ])

  return {
    vendorDetails,
    setVendorDetails,
    solicitorDetails,
    setSolicitorDetails,
    hasUnsavedChanges,
    saveState,
    isSaving,
    isPending,
    isError,
    handleSaveChanges,
    rexDisconnectedModal,
  }
}

export default useVendorDetailsSectionState
